import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { IdentityService } from '@lsq-frontend/fasttrack-shared/features/identity-service';
import { AccountPaymentsListResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/list-account-payments';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import {
    SortDirection,
    TransfersInput,
    TransfersResponse,
    TransfersSortColumn,
    TransferType
} from '../../model/models';

export class AccountPaymentsListRequest extends Mk2Request {
    accountPaymentsListPayerEnums?: ('BUYER' | 'DEPOSIT_ACCOUNT' | 'LSQ' | 'UNKNOWN_PAYER')[];
    buyerKeys?: string[];
    dateRangeEnd?: string;
    dateRangeStart?: string;
    pagination?: Pagination;
    searchValue?: string;
    searchInvoiceNumber?: string;

    constructor(initial?: Partial<AccountPaymentsListRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}
// TODO: Move FeatureFlagService into angular-shared
@Injectable({ providedIn: 'root' })
//Currently We don't have payments view in buyer account
export class ListAccountPaymentsService extends ApiService<
    AccountPaymentsListRequest,
    AccountPaymentsListResponse
> {
    public requestType = AccountPaymentsListRequest;
    public responseType = AccountPaymentsListResponse;
    public endpointName = 'BuyerAccount/PaymentsList/ListAccountPayments';
    public serviceName = 'financing';
    private _payLaterIsOn = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService,
        private _userService: UserService,
        private _identityService: IdentityService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._payLaterIsOn = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/PaymentsList/ListAccountPayments';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public get endpointUrl(): string {
        return this._payLaterIsOn ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public requestMapper(
        req: AccountPaymentsListRequest
    ): { query: string } | AccountPaymentsListRequest {
        if (this._payLaterIsOn) {
            // TODO: Implement trace number search
            // TODO: Implement date range filtering
            const input: TransfersInput = {
                invoiceNumber: req.searchInvoiceNumber.trim(),
                transferIds: [],
                statuses: [],
                companyFilter: {
                    buyerIds: this._identityService.isScfBuyer()
                        ? [this._userService.getAlternateCompanyKey()]
                        : req.buyerKeys,
                    buyerAccountIds: this._identityService.isScfBuyerAccount()
                        ? [this._userService.getAlternateCompanyKey()]
                        : [],
                    supplierIds: []
                },
                amountOrTraceNumberSearch: req.searchValue,
                transferTypes: [],
                pagination: {
                    skip: req.pagination.skip,
                    take: req.pagination.take,
                    sortColumn: new EnumType(
                        (req.pagination.sortColumn ?? null) as TransfersSortColumn
                    ),
                    sortDirection: new EnumType(
                        (req.pagination.sortDirection ?? (null as unknown)) as SortDirection
                    )
                }
            };

            if (req.accountPaymentsListPayerEnums?.length > 0) {
                if (req.accountPaymentsListPayerEnums.includes('LSQ')) {
                    input.transferTypes.push(TransferType.EARLY_PAYMENT);
                }
                if (
                    req.accountPaymentsListPayerEnums.includes('BUYER') ||
                    req.accountPaymentsListPayerEnums.includes('DEPOSIT_ACCOUNT')
                ) {
                    input.transferTypes.push(TransferType.BUYER_TO_CREDITOR);
                    // TODO: We probably should only show 1 of the TPM transfers, but which one?
                    // input.transferTypes.push(TransferType.BUYER_TO_FBO);
                    input.transferTypes.push(TransferType.FBO_TO_CREDITOR);
                }
            }

            return {
                query: jsonToGraphQLQuery({
                    query: {
                        transfers: {
                            __args: {
                                input
                            },
                            pagination: {
                                skip: true,
                                take: true,
                                sortColumn: true,
                                sortDirection: true
                            },
                            rows: {
                                id: true,
                                totalApprovedInvoiceAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                invoiceCount: true,
                                buyerPaymentAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                transferDate: true,
                                recipientType: true,
                                traceNumber: true
                            },
                            totals: {
                                transferCount: true
                            }
                        }
                    }
                })
            };
        }
        return req;
    }

    public responseMapper(
        res: AccountPaymentsListResponse | TransfersResponse
    ): AccountPaymentsListResponse {
        if (this._payLaterIsOn) {
            const { transfers } = (res as GraphQLResponse<TransfersResponse>).data;
            return {
                // TODO: Might need to map sortColumn to whatever the UI expects
                pagination: transfers.pagination as unknown as Pagination,
                rows: transfers.rows.map((transfer) => {
                    let payer = 'UNKNOWN_PAYER';
                    let paymentType = 'UNKNOWN_PAYMENT_TYPE';
                    if (transfer.transferType === TransferType.BUYER_TO_CREDITOR) {
                        payer = 'BUYER';
                        paymentType = 'EARLY_PAY_REMITTANCE';
                    }
                    if (transfer.transferType === TransferType.BUYER_TO_FBO) {
                        payer = 'BUYER';
                        paymentType = 'INVOICE_TERM';
                    }
                    if (transfer.transferType === TransferType.EARLY_PAYMENT) {
                        payer = 'LSQ';
                        paymentType = 'EARLY_PAYMENT';
                    }
                    if (transfer.transferType === TransferType.FBO_TO_CREDITOR) {
                        payer = 'DEPOSIT_ACCOUNT';
                        paymentType = 'INVOICE_TERM';
                    }
                    if (transfer.transferType === TransferType.ON_TERM_PAYMENT) {
                        payer = 'LSQ';
                        paymentType = 'PAY_LATER_PAYMENT';
                    }
                    if (transfer.transferType === TransferType.FUNDER_TO_FBO) {
                        payer = 'LSQ';
                        paymentType = 'PAY_LATER_PAYMENT';
                    }

                    return {
                        batchKey: transfer.id,
                        invoiceAmount: transfer.totalApprovedInvoiceAmount,
                        numberOfInvoices: transfer.invoiceCount,
                        payer,
                        paymentAmount: transfer.buyerPaymentAmount,
                        paymentDate: transfer.transferDate,
                        paymentType,
                        recipient: transfer.recipientType,
                        referenceNumber: transfer.traceNumber
                    };
                }),
                totalCount: transfers.totals.transferCount
                // ignoring searchValue, seems to not be in use
            } as AccountPaymentsListResponse;
        }
        return res as AccountPaymentsListResponse;
    }
}
